import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Typography,
  Row,
  Col,
  Button,
  Statistic,
  Select,
  DatePicker,
  message,
  Empty,
  Spin,
  Alert,
  Badge,
  Tooltip,
  Progress,
  Space,
  Divider,
  Table,
  Tag,
  Tabs,
  Radio,
  Switch
} from 'antd';
import {
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  DashboardOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FilterOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  TrophyOutlined,
  ThunderboltOutlined,
  FireOutlined,
  StarOutlined
} from '@ant-design/icons';
import AppHeader from '../../components/Header';
import { analyticsAPI, applicationAPI } from '../../services/api';
import LoadingAnimation from '../../components/LoadingAnimation';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import './Analytics.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface Application {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'suspended';
}

interface AnalyticsData {
  summary: {
    total_requests: number;
    success_count: number;
    failure_count: number;
    success_rate: number;
    avg_processing_time: number;
    avg_confidence_score: number;
    unique_applications: number;
  };
  charts: {
    success_failure: Array<{ name: string; value: number; percentage: number }>;
    document_types: Array<{ name: string; value: number; percentage: number }>;
    applications: Array<{ name: string; value: number; percentage: number }>;
    time_trends: Array<{ date: string; success: number; failure: number; total: number }>;
  };
  performance_metrics: {
    fastest_request: number;
    slowest_request: number;
    peak_hour: string;
    busiest_day: string;
    quality_score: number;
  };
  latest_requests: Array<{
    id: string;
    timestamp: string;
    application_name: string;
    document_type: string;
    status: string;
    processing_time: number;
    confidence_score: number;
  }>;
  top_applications: Array<{
    name: string;
    request_count: number;
    success_rate: number;
    avg_processing_time: number;
  }>;
}

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [dateFilter, setDateFilter] = useState<string>('7d');
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const loadingRef = useRef({ applications: false, analytics: false });

  useEffect(() => {
    loadApplications();
    loadAnalyticsData();
  }, []);

  useEffect(() => {
    // Reload analytics when filters change (but not on mount)
    if (loadingRef.current.analytics) return;
    loadAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const loadAnalyticsData = async () => {
    if (loadingRef.current.analytics) return;
    
    try {
      loadingRef.current.analytics = true;
      setLoading(true);
      console.log('Loading global analytics data with filter:', dateFilter);
      
      // Always load global analytics
      const response = await analyticsAPI.getCompanyAnalytics({ date_filter: dateFilter });      console.log('Analytics API response:', response);
      console.log('Raw latest_requests data:', response.data?.data?.latest_requests);
      
      if (response.data && response.data.success) {
        // Ensure the data structure has all required fields with defaults
        const data = response.data.data;
        console.log('Processing analytics data:', data);
        const normalizedData = {
          summary: {
            total_requests: data?.summary?.total_requests || 0,
            success_count: data?.summary?.success_count || 0,
            failure_count: data?.summary?.failure_count || 0,
            success_rate: data?.summary?.success_rate || 0,
            avg_processing_time: data?.summary?.avg_processing_time || 0,
            avg_confidence_score: data?.summary?.avg_confidence_score || 0,
            unique_applications: data?.summary?.unique_applications || 0,
          },
          charts: {
            success_failure: (data?.charts?.success_failure || []).map((item: any) => ({
              name: String(item?.name || 'Unknown'),
              value: Number(item?.value || 0),
              percentage: Number(item?.percentage || 0),
            })),
            document_types: (data?.charts?.document_types || []).map((item: any) => ({
              name: String(item?.name || 'Unknown'),
              value: Number(item?.value || 0),
              percentage: Number(item?.percentage || 0),
            })),
            applications: (data?.charts?.applications || []).map((item: any) => ({
              name: String(item?.name || 'Unknown'),
              value: Number(item?.value || 0),
              percentage: Number(item?.percentage || 0),
            })),
            time_trends: (data?.charts?.time_trends || []).map((item: any) => ({
              date: String(item?.date || new Date().toISOString()),
              success: Number(item?.success || 0),
              failure: Number(item?.failure || 0),
              total: Number(item?.total || 0),
            })),
          },
          performance_metrics: {
            fastest_request: typeof data?.performance_metrics?.fastest_request === 'object' 
              ? (data.performance_metrics.fastest_request?.processing_time_ms || data.performance_metrics.fastest_request?.processing_time || 0)
              : (data?.performance_metrics?.fastest_request || 0),
            slowest_request: typeof data?.performance_metrics?.slowest_request === 'object' 
              ? (data.performance_metrics.slowest_request?.processing_time_ms || data.performance_metrics.slowest_request?.processing_time || 0)
              : (data?.performance_metrics?.slowest_request || 0),
            peak_hour: typeof data?.performance_metrics?.peak_hour === 'object' 
              ? JSON.stringify(data.performance_metrics.peak_hour) 
              : (data?.performance_metrics?.peak_hour || 'N/A'),
            busiest_day: typeof data?.performance_metrics?.busiest_day === 'object' 
              ? JSON.stringify(data.performance_metrics.busiest_day) 
              : (data?.performance_metrics?.busiest_day || 'N/A'),
            quality_score: data?.performance_metrics?.quality_score || 0,
          },
          latest_requests: (data?.latest_requests || []).map((req: any) => ({
            id: req?.id || '',
            timestamp: req?.timestamp || new Date().toISOString(),
            application_name: req?.application_name || 'Unknown',
            document_type: req?.document_type || 'Unknown',
            status: req?.status || 'unknown',
            processing_time: req?.processing_time_ms || req?.processing_time || 0,
            confidence_score: req?.confidence_score || 0,
          })),
          top_applications: (data?.top_applications || []).map((app: any) => ({
            name: app?.application_name || app?.name || 'Unknown',
            request_count: app?.request_count || 0,
            success_rate: app?.success_rate || 0,
            avg_processing_time: app?.avg_processing_time || 0,
          })),
        };
        console.log('Normalized analytics data:', normalizedData);
        setAnalyticsData(normalizedData);
      } else {
        console.log('Analytics response:', response.data);
        message.error(response.data?.message || 'Failed to load analytics data');
        setAnalyticsData(null);
      }
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load analytics data';
      message.error(errorMessage);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
      loadingRef.current.analytics = false;
    }
  };

  const loadApplications = async () => {
    if (loadingRef.current.applications) return;
    
    try {
      loadingRef.current.applications = true;
      console.log('Loading applications...');
      const response = await applicationAPI.getApplications();
      console.log('Applications API response:', response);
      
      if (response.data && response.data.success) {
        const apps = response.data.data || []; // Fix: use data instead of applications
        console.log('Loaded applications:', apps);
        setApplications(apps);
      } else {
        console.log('Applications response (no success):', response.data);
        setApplications([]);
      }
    } catch (error: any) {
      console.error('Error loading applications:', error);
      console.error('Error details:', error.response?.data);
      setApplications([]);
    } finally {
      loadingRef.current.applications = false;
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalyticsData();
    setRefreshing(false);
    message.success('Analytics data refreshed');
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const params = {
        format,
        date_filter: dateFilter,
        include_details: true
      };
      
      const response = await analyticsAPI.exportAnalytics(params);
      
      if (format === 'csv') {
        // Handle blob response for CSV
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics_${dateFilter}_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Handle JSON response
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const exportFileDefaultName = `analytics_${dateFilter}_${Date.now()}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
      
      message.success(`Analytics exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting analytics:', error);
      message.error('Failed to export analytics');
    }
  };

  const getSuccessFailureChartData = () => {
    if (!analyticsData?.charts?.success_failure || analyticsData.charts.success_failure.length === 0) {
      return null;
    }
    
    return {
      labels: analyticsData.charts.success_failure.map(item => String(item?.name || 'Unknown')),
      datasets: [
        {
          data: analyticsData.charts.success_failure.map(item => Number(item?.value || 0)),
          backgroundColor: [
            '#52c41a', // success - green
            '#ff4d4f', // failure - red
          ],
          borderColor: [
            '#389e0d',
            '#cf1322',
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  const getDocumentTypesChartData = () => {
    if (!analyticsData?.charts?.document_types || analyticsData.charts.document_types.length === 0) {
      return null;
    }
    
    const colors = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#eb2f96', '#f5222d', '#13c2c2', '#a0d911'];
    
    return {
      labels: analyticsData.charts.document_types.map(item => String(item?.name || 'Unknown')),
      datasets: [
        {
          data: analyticsData.charts.document_types.map(item => Number(item?.value || 0)),
          backgroundColor: colors.slice(0, analyticsData.charts.document_types.length),
          borderColor: colors.slice(0, analyticsData.charts.document_types.length).map(color => color + 'CC'),
          borderWidth: 2,
        },
      ],
    };
  };

  const getTimeTrendsChartData = () => {
    if (!analyticsData?.charts?.time_trends || analyticsData.charts.time_trends.length === 0) {
      return null;
    }
    
    return {
      labels: analyticsData.charts.time_trends.map(item => {
        // Ensure the date is a string and can be parsed
        const dateStr = typeof item.date === 'string' ? item.date : JSON.stringify(item.date);
        try {
          return new Date(dateStr).toLocaleDateString();
        } catch {
          return String(dateStr);
        }
      }),
      datasets: [
        {
          label: 'Success',
          data: analyticsData.charts.time_trends.map(item => Number(item?.success || 0)),
          borderColor: '#52c41a',
          backgroundColor: '#52c41a33',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Failure',
          data: analyticsData.charts.time_trends.map(item => Number(item?.failure || 0)),
          borderColor: '#ff4d4f',
          backgroundColor: '#ff4d4f33',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Total',
          data: analyticsData.charts.time_trends.map(item => Number(item?.total || 0)),
          borderColor: '#1890ff',
          backgroundColor: 'transparent',
          tension: 0.4,
          fill: false,
          borderDash: [5, 5],
        },
      ],
    };
  };

  const getApplicationsChartData = () => {
    if (!analyticsData?.charts?.applications || analyticsData.charts.applications.length === 0) {
      return null;
    }
    
    const colors = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#eb2f96'];
    
    return {
      labels: analyticsData.charts.applications.map(item => String(item?.name || 'Unknown')),
      datasets: [
        {
          label: 'Requests',
          data: analyticsData.charts.applications.map(item => Number(item?.value || 0)),
          backgroundColor: colors.slice(0, analyticsData.charts.applications.length),
          borderColor: colors.slice(0, analyticsData.charts.applications.length).map(color => color + 'CC'),
          borderWidth: 2,
        },
      ],
    };
  };

  const renderDashboardTab = () => (
    <div>
      {/* Key Metrics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Requests"
              value={analyticsData?.summary.total_requests || 0}
              prefix={<BarChartOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={analyticsData?.summary.success_rate || 0}
              precision={2}
              suffix="%"
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: (analyticsData?.summary.success_rate || 0) >= 95 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Processing Time"
              value={analyticsData?.summary.avg_processing_time || 0}
              precision={2}
              suffix="ms"
              prefix={<ClockCircleOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Confidence"
              value={analyticsData?.summary.avg_confidence_score || 0}
              precision={2}
              suffix="%"
              prefix={<StarOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: (analyticsData?.summary.avg_confidence_score || 0) >= 90 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Performance Indicators */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Performance Metrics">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Fastest Request"
                  value={analyticsData?.performance_metrics.fastest_request || 0}
                  precision={2}
                  suffix="ms"
                  prefix={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Slowest Request"
                  value={analyticsData?.performance_metrics.slowest_request || 0}
                  precision={2}
                  suffix="ms"
                  prefix={<ClockCircleOutlined style={{ color: '#ff4d4f' }} />}
                />
              </Col>
              <Col span={12}>
                <div>
                  <Text strong>Peak Hour: </Text>
                  <Badge count={String(analyticsData?.performance_metrics.peak_hour || 'N/A')} 
                         style={{ backgroundColor: '#1890ff' }} />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text strong>Busiest Day: </Text>
                  <Badge count={String(analyticsData?.performance_metrics.busiest_day || 'N/A')} 
                         style={{ backgroundColor: '#722ed1' }} />
                </div>
              </Col>
            </Row>
            <Divider />
            <div>
              <Text strong>Quality Score: </Text>
              <Progress 
                percent={analyticsData?.performance_metrics.quality_score || 0} 
                status={(analyticsData?.performance_metrics.quality_score || 0) >= 90 ? 'success' : 'normal'}
                strokeColor={(analyticsData?.performance_metrics.quality_score || 0) >= 90 ? '#52c41a' : '#faad14'}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Top Applications">
            {analyticsData?.top_applications && analyticsData.top_applications.length > 0 ? (
              <div>
                {analyticsData.top_applications.slice(0, 5).map((app, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong>{app.name}</Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Badge count={app.request_count} style={{ backgroundColor: '#1890ff' }} />
                        <Button 
                          size="small" 
                          type="link"
                          onClick={() => {
                            // Find the application by name to get its ID
                            const application = applications.find(a => a.name === app.name);
                            if (application) {
                              navigate(`/api-management/applications/${application.id}?tab=analytics`);
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Success: {(app.success_rate || 0).toFixed(1)}% • 
                        Avg Time: {(app.avg_processing_time || 0).toFixed(2)}ms
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No application data available" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Latest Requests */}
      <Card title="Latest Requests" style={{ marginBottom: 24 }}>
        {analyticsData?.latest_requests && analyticsData.latest_requests.length > 0 ? (
          <Table
            dataSource={analyticsData.latest_requests}
            columns={[
              {
                title: 'Timestamp',
                dataIndex: 'timestamp',
                key: 'timestamp',
                render: (timestamp: string) => new Date(timestamp).toLocaleString(),
                width: 180,
              },
              {
                title: 'Application',
                dataIndex: 'application_name',
                key: 'application_name',
              },
              {
                title: 'Document Type',
                dataIndex: 'document_type',
                key: 'document_type',
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => {
                  const normalizedStatus = status?.toLowerCase();
                  const isSuccess = normalizedStatus === 'success' || normalizedStatus === 'completed';
                  return (
                    <Tag color={isSuccess ? 'green' : 'red'}>
                      {status?.toUpperCase() || 'UNKNOWN'}
                    </Tag>
                  );
                },
              },
              {
                title: 'Processing Time',
                dataIndex: 'processing_time',
                key: 'processing_time',
                render: (time: number) => `${(time || 0).toFixed(2)}ms`,
              },
              {
                title: 'Confidence',
                dataIndex: 'confidence_score',
                key: 'confidence_score',
                render: (score: number) => (
                  <Progress 
                    percent={score || 0} 
                    size="small" 
                    status={score >= 90 ? 'success' : 'normal'}
                    format={() => `${(score || 0).toFixed(1)}%`}
                  />
                ),
              },
            ]}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
          />
        ) : (
          <Empty description="No recent requests" />
        )}
      </Card>
    </div>
  );

  const renderChartsTab = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card title="Success vs Failure">
          {analyticsData?.charts?.success_failure && analyticsData.charts.success_failure.length > 0 && getSuccessFailureChartData() ? (
            <div style={{ height: 300 }}>
              <Doughnut data={getSuccessFailureChartData()!} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                  },
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const data = analyticsData.charts.success_failure[context.dataIndex];
                        if (!data) return 'No data';
                        const name = String(data.name || 'Unknown');
                        const value = Number(data.value || 0);
                        const percentage = Number(data.percentage || 0);
                        return `${name}: ${value} (${percentage.toFixed(1)}%)`;
                      }
                    }
                  }
                }
              }} />
            </div>
          ) : (
            <Empty description="No success/failure data available" />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Document Types Distribution">
          {analyticsData?.charts?.document_types && analyticsData.charts.document_types.length > 0 && getDocumentTypesChartData() ? (
            <div style={{ height: 300 }}>
              <Pie data={getDocumentTypesChartData()!} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                  },
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const data = analyticsData.charts.document_types[context.dataIndex];
                        if (!data) return 'No data';
                        const name = String(data.name || 'Unknown');
                        const value = Number(data.value || 0);
                        const percentage = Number(data.percentage || 0);
                        return `${name}: ${value} (${percentage.toFixed(1)}%)`;
                      }
                    }
                  }
                }
              }} />
            </div>
          ) : (
            <Empty description="No document type data available" />
          )}
        </Card>
      </Col>
      <Col xs={24}>
        <Card title="Time Trends">
          {analyticsData?.charts?.time_trends && analyticsData.charts.time_trends.length > 0 && getTimeTrendsChartData() ? (
            <div style={{ height: 400 }}>
              <Line data={getTimeTrendsChartData()!} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                  }
                }
              }} />
            </div>
          ) : (
            <Empty description="No time trend data available" />
          )}
        </Card>
      </Col>
      <Col xs={24}>
        <Card title="Applications Usage">
          {analyticsData?.charts?.applications && analyticsData.charts.applications.length > 0 && getApplicationsChartData() ? (
              <div style={{ height: 300 }}>
                <Bar data={getApplicationsChartData()!} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    }
                  }
                }} />
              </div>
            ) : (
              <Empty description="No application usage data available" />
            )}
          </Card>
        </Col>
    </Row>
  );

  if (loading) {
    return (
      <Layout className="layout-container">
        <AppHeader />
        <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
          <LoadingAnimation
            type="orbit"
            message="Loading comprehensive analytics..."
            size="large"
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="layout-container">
      <AppHeader />
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            Analytics Dashboard
          </Title>
          <Paragraph>
            Monitor your OCR API usage, performance metrics, and trends across applications.
          </Paragraph>
        </div>

        {/* Controls */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text strong style={{ marginRight: 8 }}>Time Period:</Text>
                <Select
                  value={dateFilter}
                  onChange={setDateFilter}
                  style={{ width: '100%' }}
                >
                  <Option value="1d">Last 24 hours</Option>
                  <Option value="7d">Last 7 days</Option>
                  <Option value="30d">Last 30 days</Option>
                  <Option value="90d">Last 90 days</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleRefresh}
                  loading={refreshing}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
            
            <Col xs={24} sm={12} md={8}>
              <div style={{ textAlign: 'right' }}>
                <Button 
                  type="primary" 
                  icon={<BarChartOutlined />}
                  onClick={() => navigate('/api-management')}
                >
                  View Application Analytics
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Main Content */}
        {analyticsData ? (
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
              {
                key: 'dashboard',
                label: (
                  <span>
                    <DashboardOutlined />
                    Dashboard
                  </span>
                ),
                children: renderDashboardTab(),
              },
              {
                key: 'charts',
                label: (
                  <span>
                    <BarChartOutlined />
                    Charts
                  </span>
                ),
                children: renderChartsTab(),
              },
            ]}
          />
        ) : (
          <Card>
            <Empty 
              description="No analytics data available"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleRefresh}>
                Try Again
              </Button>
            </Empty>
          </Card>
        )}
      </Content>
    </Layout>
  );
};

export default Analytics;
